/* eslint-disable complexity */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	CircularProgress,
	Typography,
	Skeleton,
} from '@mui/material';
import { useMutation, UseQueryResult } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useBeforeunload } from 'react-beforeunload';
import axios, { AxiosError } from 'axios';
import { isPhoneNumber, isEmail } from 'class-validator';
import { FormikErrors, useFormik, FormikProvider } from 'formik';
import { CheckRounded } from '@mui/icons-material';
import { flatten, isEmpty, reduce, findKey, map, compact, merge } from 'lodash-es';
import {
	Colors,
	EventBackground,
	getReponseDataFromAxiosError,
	useStyles,
	Showable,
	WarningModal,
	checkoutFromSubmitButtonId,
	handleE2ECardPayment,
} from '@sweatpals/react-ui-kit';
import {
	CreateEventTicketDto,
	EventToUserPeriodTypeEnum,
	FileVariantsEnum,
	GetPricesAndFeesDto,
	OrderDto,
	waitAsync,
	VerificationInstance,
	IntakeFormReservedFieldNames,
	EventTicketsTotalsWithReservationsDto,
	EventTicketsReservationDto,
	IntakeFormUserDataResults,
	PublicJoinEventBody,
	UserDto,
	MembershipDto,
	UserToMembershipDto,
	CreatePaymentIntentV2,
	EventPriceDto,
	UserToEventBundleDto,
	EventBundleWithStatsDto
} from '@sweatpals/shared';
import { useRouter } from 'next/router';
import { StripeElements, PaymentIntentConfirmParams, ConfirmKlarnaPaymentData, PaymentIntentResult } from '@stripe/stripe-js';
import * as yup from 'yup';
import { EventType } from '../../../services/types/event-types';
import {
	authService,
	eventsService,
	paymentService,
	stripeService,
	eventTicketsReservationService,
} from '../../../services';
import { getPublicFileUrl } from '../../../utils/file.utils';
import { checkoutFormStyles, commonCheckoutFormStyles } from './checkout-form.styles';
import { CoreTextField } from '../core-textfield';
import { ArrowBackIcon } from '../../icons';
import useDebounce from '../../../utils/hooks/useDebounce';
import { useEventUpsertReservations } from '../../../hooks/events/use-event-upsert-reservations.hook';
import { authStore } from '../../../stores';
import UserAvatar from '../../images/UserAvatar';
import { MembershipFormModal } from '../membership-form-modal/membership-form-modal.component';
import { EventBundleFormModal } from '../event-bundle-form-modal/event-bundle-form-modal.component';
import { OrderModal } from '../order';
import { getPrefilledIntakeFormResults, TicketForm } from './ticket-form.component';
import { EventDetails } from './event-details.component';
import {
	CheckoutFormDto,
	EventPricesStepsDto,
	IntakeFormDto,
} from '../../../interfaces/checkout-form.dto';
import SignInForm from '../forms/signin-form/signin-form.component';
import { PaymentStep } from './payment-step.component';
import { useTickets } from '../../../hooks/events/use-tickets.hook';
import { useAnalytics } from '../../../hooks/use-analytics.hook';
import { APP_ENV, HOME_PAGE_URL } from '../../../constants';
import { prepareIntakeFormValueToSave } from '../../../utils/intake-form.utils';

const calculateDefaultIntakeForm = (firstIntakeForm: IntakeFormDto, intakeFormCommonFieldNames: string[]) => {
	const intakeFromResult = {};

	if (firstIntakeForm) {
		map(firstIntakeForm.result, (value, key) => {
			if (intakeFormCommonFieldNames.includes(key)) {
				intakeFromResult[key] = value;
			}
		});
	}

	return {
		isPrefilled: false,
		result: intakeFromResult
	};
}

enum WarningModalTypeEnum {
	TicketReservations = 'ticket-reservations',
	Order = 'order',
}

export interface CheckoutFormProps {
	eventId?: string;
	instance?: string;
	showSkeleton?: boolean;
	eventData?: EventType;
	hostData?: UserDto;
	prices?: EventPriceDto[];
	publishedPrices?: EventPriceDto[];
	hostMemberships?: MembershipDto[];
	hostEventBundles?: EventBundleWithStatsDto[];
	boughtEventBundle?: UserToEventBundleDto;
	boughtMembership?: UserToMembershipDto;
	hostEvents?: EventType[];
	preselectedTickets?: CreateEventTicketDto[];
	freeSpotsCount?: number;
	editTiersDisabled?: boolean;
	hideEventDetails?: boolean;
	Header?: FC<{ clearUserEventTicketReservations(): Promise<void> }>
	eventTicketsCountAndSpots?: EventTicketsTotalsWithReservationsDto;
	userTicketReservations?: EventTicketsReservationDto[];
	refetchEvent(): Promise<UseQueryResult<EventType>>;
	refetchEventTicketsCountAndSpots(): Promise<UseQueryResult<EventTicketsTotalsWithReservationsDto>>;
	refetchUserEventTicketReservations(): Promise<UseQueryResult<EventTicketsReservationDto[]>>;
	refetchUserMemberships?(): Promise<UseQueryResult<UserToMembershipDto[]>>;
	refetchUserEventBundles?(): Promise<UseQueryResult<UserToEventBundleDto[]>>;
	onSubmit?(order: OrderDto): Promise<void> | void;
	onWarningModalClose?(): void;
}


export const CheckoutForm: FC<CheckoutFormProps> = props => {
	const {
		eventId,
		instance,
		preselectedTickets,
		eventData,
		hostData,
		hostMemberships,
		boughtMembership,
		hostEvents,
		hideEventDetails,
		hostEventBundles,
		boughtEventBundle,
		refetchUserEventBundles,
		showSkeleton = false,
		prices,
		Header,
		onSubmit,
		onWarningModalClose,
		refetchEvent,
		refetchUserEventTicketReservations,
		refetchEventTicketsCountAndSpots,
		refetchUserMemberships,
		...eventDetailsProps
	} = props;
	const {
		intakeFormSettings,
		isDonationBased,
		background,
		additionalData: additionalEventData,
		minimalDonationAmount,
	} = eventData ?? {};

	const { query } = useRouter();
	const isE2ETest = !!query['playwright'] && APP_ENV !== 'main';

	const { logEventTyped, setGAUserId } = useAnalytics();

	const paymentMethodTypes = useMemo(() => {
		const methods = ['card'];

		if (!eventData) return methods;

		if (eventData?.additionalData?.allowKlarnaPayments) {
			methods.push('klarna');
		}

		return methods;
	}, [eventData]);

	const minDonation = useMemo(
		() => (typeof minimalDonationAmount === 'number' && minimalDonationAmount > 0 ? minimalDonationAmount : 0),
		[minimalDonationAmount]
	);
	const [checkoutStep, setCheckoutStep] = useState(1);
	const [signInFormIsVisible, setSignInFormIsVisible] = useState(false);
	const [isPolicyAgreed, setIsPolicyAgreed] = useState(true);
	const [isTiersEditable, setIsTearsEditable] = useState(false);
	const [warningModalType, setWarningModalType] = useState<WarningModalTypeEnum | null>(null);
	const [resendCodeTimer, setResetCodeTimer] = useState<number | null>(null);
	const [isSignIn, setIsSignIn] = useState<boolean | null>(null);
	const [verification, setVerification] = useState<null | VerificationInstance>(null);
	const [promocode, setPromocode] = useState('');
	const [isPromocodeApplied, setIsPromocodeApplied] = useState(false);
	const [priceAndFees, setPriceAndFees] = useState<GetPricesAndFeesDto | null>(null);
	const [donationAmount, setDonationAmount] = useState<number | null>(minDonation || 2000);
	const [isPaying, setIsPaying] = useState(false);
	const [validateOnChange, setValidateOnChange] = useState(false);
	const [showRecaptchaCheckbox, setShowRecaptchaCheckbox] = useState(false);
	const [registrationFailed, setRegistrationFailed] = useState(false);
	const [membershipFormModalIsOpen, setMembershipFormModalIsOpen] = useState(false);
	const [membershipOrder, setMembershipOrder] = useState<OrderDto | null>(null);
	const [membershipOrderModalIsOpen, setMembershipOrderModalIsOpen] = useState(false);
	const [eventBundleFormModalIsOpen, setEventBundleFormModalIsOpen] = useState(false);
	const [eventBundleOrder, setEventBundleOrder] = useState<OrderDto | null>(null);
	const [eventBundleOrderModalIsOpen, setEventBundleOrderModalIsOpen] = useState(false);
	const [isSomePriceInvalid, setIsSomePriceInvalid] = useState(false);

	const { debounceFunc, cancelFunc } = useDebounce(1000);
	const { enqueueSnackbar } = useSnackbar();
	const formBodyRef = useRef<HTMLDivElement | null>(null);
	const { user: currentUser } = authStore;

	const commonStyles = useStyles(commonCheckoutFormStyles, additionalEventData?.customWebCheckoutMainColor);
	const styles = useStyles(checkoutFormStyles);

	const {
		tickets,
		addTicket,
		removeTicket,
		clearTickets,
	} = useTickets({
		prices,
		initialData: preselectedTickets,
	});

	const isPaidEvent = useMemo(
		() => eventData?.isPaid && !eventData?.isDonationBased,
		[eventData?.isPaid, eventData?.isDonationBased]
	);
	const isPaymentRequired = useMemo(
		() => (eventData?.isPaid && ((priceAndFees?.fullPrice ?? 0) > 0)),
		[eventData?.isPaid, priceAndFees?.fullPrice]
	);
	// require phone verification for admin discounts or membership discounts
	const isVerificationRequired = useMemo(
		() => eventData?.isPaid && priceAndFees?.membershipDiscountAmount > 0,
		[eventData?.isPaid, priceAndFees?.membershipDiscountAmount]
	);
	const ticketsQuantity = useMemo(
		() => (isPaidEvent ? (tickets ?? []).reduce((acc, { quantity }) => acc + quantity, 0) : 1),
		[isPaidEvent, tickets]
	);
	const preselectedTicketsQuantity = useMemo(
		() => (isPaidEvent ? (preselectedTickets ?? []).reduce((acc, { quantity }) => acc + quantity, 0) : 1),
		[isPaidEvent, preselectedTickets]
	);
	const isMultipleIntakeForm = useMemo(() => {
		if (
			!intakeFormSettings?.sections
			|| intakeFormSettings?.showSectionsOnceForMultipleTickets
		) {
			return false;
		}

		const fields = flatten(intakeFormSettings?.sections.map(s => s.fields));
		const individualFields = fields.filter(field => !field.isCommonForAllTickets);

		return individualFields.length && (ticketsQuantity > 1);
	}, [intakeFormSettings?.sections, intakeFormSettings?.showSectionsOnceForMultipleTickets, ticketsQuantity]);
	const paymentStep = useMemo(
		() => (isMultipleIntakeForm ? ticketsQuantity + 1 : 1),
		[isMultipleIntakeForm, ticketsQuantity]
	);
	const verifyCodeStep = useMemo(
		() => (isMultipleIntakeForm ? ticketsQuantity + 2 : 2),
		[isMultipleIntakeForm, ticketsQuantity]
	);
	const eventPricesSteps = useMemo<EventPricesStepsDto>(() => {
		if (!tickets?.length) {
			return {};
		}

		return tickets.reduce<Record<string, { firstStep: number; lastStep: number }>>((acc, ticket, index) => {
			const prevEventPriceLastStep = tickets.slice(0, index).reduce((acc, { quantity }) => acc + quantity, 0);

			return {
				...acc,
				[ticket.eventPriceId]: {
					firstStep: prevEventPriceLastStep + 1,
					lastStep: tickets.slice(0, index + 1).reduce((acc, { quantity }) => acc + quantity, 0),
				}
			};
		}, {});
	}, [tickets]);
	const currentEventPrice = useMemo(() => {
		const eventPriceId = findKey(
			eventPricesSteps,
			({ firstStep, lastStep }) => (firstStep <= checkoutStep && checkoutStep <= lastStep),
		);

		return prices?.find(price => price.id === eventPriceId);
	}, [checkoutStep, eventPricesSteps, prices]);
	const currentIntakeFormIndex = useMemo(
		() => (ticketsQuantity >= checkoutStep ? checkoutStep - 1 : 0),
		[checkoutStep, ticketsQuantity]
	);
	const intakeFormFields = useMemo(() => [
		...flatten((intakeFormSettings?.sections ?? []).map(section => section.fields)),
		...flatten((intakeFormSettings?.waiverSections ?? []).map(section => section.fields)),
	], [intakeFormSettings?.sections, intakeFormSettings?.waiverSections]);
	const intakeFormReservedFieldNames = useMemo<string[]>(() => [
		IntakeFormReservedFieldNames.FullName,
		IntakeFormReservedFieldNames.Email,
		IntakeFormReservedFieldNames.PhoneNumber,
	], []);
	const intakeFormCommonFieldNames = useMemo(
		() => intakeFormFields.filter(f => f.isCommonForAllTickets).map(f => f.name),
		[intakeFormFields]
	);
	const publicHostMemberships = useMemo(() => hostMemberships?.filter(m => !m.isUnlisted), [hostMemberships]);

	const prepareIntakeFormResultsToSave = useCallback(
		(intakeFormResults: IntakeFormDto[]): IntakeFormUserDataResults[] => intakeFormResults.map(
			({ result, isPrefilled }, index) => {
				const firstIntakeForm = intakeFormResults[0];
				const defaultIntakeForm = calculateDefaultIntakeForm(firstIntakeForm, intakeFormCommonFieldNames)
				const prefilledData = getPrefilledIntakeFormResults({ isPrefilled, defaultIntakeForm, firstIntakeForm, intakeFormFields, currentEventPrice });

				const fullData = merge(prefilledData, result);

				const prepared = reduce(fullData, (acc, value, key) => {
					const field = intakeFormFields.find(f => f.name === key);

					return { ...acc, [key]: prepareIntakeFormValueToSave(value, field) };
				}, { skipSigningForm: !isPrefilled && index !== 0 });

				return prepared;
			}
		),
		[intakeFormFields, intakeFormCommonFieldNames, currentEventPrice]
	);

	const {
		isLoading: isPaymentIntentLoading,
		mutateAsync: createPaymentIntent,
	} = useMutation(
		async ({ intakeForms, ...data }: CheckoutFormDto) => {
			if (!eventId || !instance) {
				return;
			}

			const generalData: CreatePaymentIntentV2 = {
				eventId,
				instanceDate: instance,
				intakeFormResults: intakeForms && prepareIntakeFormResultsToSave(intakeForms),
				supportedPaymentMethodTypes: paymentMethodTypes,
				...(isDonationBased ? { donationAmount: donationAmount ?? 0 } : { tickets, applyPromocode: promocode }),
				...(boughtEventBundle ? { eventBundleId: boughtEventBundle.eventBundleId } : {}),
			};

			if (currentUser) {
				return paymentService.createPaymentIntentV2(generalData);
			}

			return paymentService.createPublicPaymentIntentV2({
				...data,
				...generalData,
				code: checkoutStep === verifyCodeStep ? data.code : undefined,
			})
		},
		{
			onError: (err: Error) => {
				const data = getReponseDataFromAxiosError(err);

				if (data.statusCode === 404) {
					setIsSomePriceInvalid(true);

					return;
				}

				if (err instanceof AxiosError && err.response?.status === 403) {
					setShowRecaptchaCheckbox(err.response?.status === 403);
				}

				const msg = data?.message || err.message;
				enqueueSnackbar(msg, { variant: 'error' });
			},
		}
	);

	const {
		reservationTimer,
		ticketIsReserved,
		upsertReservations: upsertUserEventTicketReservations,
		clearReservations: clearUserEventTicketReservations,
	} = useEventUpsertReservations({
		eventId,
		instance,
		tickets,
		refetchEventTicketsCountAndSpots,
		refetchUserEventTicketReservations,
		upsertUserEventTicketReservations: eventTicketsReservationService.upsertUserEventTicketReservations,
		eventData,
		onErrorReservationTimeEnded: () => {
			setWarningModalType(WarningModalTypeEnum.TicketReservations);
		},
		onError: error => {
			const data = getReponseDataFromAxiosError(error);

			enqueueSnackbar(data?.message || error.message, { variant: 'error' });
		},
	});

	useBeforeunload(eventData?.ticketReservationTimeMinutes ? clearUserEventTicketReservations : null);

	const {
		data: newPriceAndFees,
		isLoading: isPriceAndFeesLoading,
		mutateAsync: getPriceAndFees,
	} = useMutation(
		async (promocode?: string) => {
			if (!eventId || !instance) {
				return;
			}

			const getPrices = currentUser ? paymentService.getPrices : paymentService.getPublicPrices;

			const priceAndFees = await getPrices({
				eventId,
				eventInstanceDate: instance,
				...(isDonationBased ? { donationAmount: donationAmount ?? 0 } : { tickets, applyPromocode: promocode }),
			});

			setIsPromocodeApplied(!!priceAndFees.discountIds?.length);

			return priceAndFees;
		},
		{
			onError: (err: Error) => {
				const data = getReponseDataFromAxiosError(err);

				if (data.statusCode === 404) {
					setIsSomePriceInvalid(true);

					return;
				}

				enqueueSnackbar(data?.message || err.message, { variant: 'error' });
			},
		}
	);

	const onSendVerificationCode = useCallback(async (phone: string) => {
		try {
			const { verification, isSignIn } = await authService.signInOrSignUpWithPhone(phone);

			setVerification(verification);
			setIsSignIn(isSignIn);
			setResetCodeTimer(60);
		} catch (error) {
			if (error instanceof AxiosError) {
				enqueueSnackbar(error.response?.data?.message || error.message);
			}

			throw error;
		}
	}, [enqueueSnackbar]);

	const onEventSubmit = useCallback(async (orderId?: string): Promise<void> => {
		try {
			if (!eventData) {
				return;
			}

			let order: OrderDto | undefined;
			let attempts = 0;

			while (!order && attempts <= 10 && orderId) {
				attempts++;
				await waitAsync(5000);
				order = await paymentService.getOrderByIdOrPaymentIntentId(orderId);
			}

			if (!order) {
				throw new Error('Cannot find an order');
			}

			setGAUserId(order.userId);
			logEventTyped('event_purchase', {
				eventId: eventData.id,
				baseEventId: eventData.baseId,
				eventInstance: eventData.instance ? new Date(eventData?.instance)?.toISOString() : undefined,
				alias: eventData.alias,
				authorId: eventData.authorId,
				amount: order.paymentDetails.amount,
				currency: order.paymentDetails.currency,
			});

			await clearUserEventTicketReservations();
			await onSubmit?.(order);
		} catch (error) {
			setWarningModalType(WarningModalTypeEnum.Order);

			throw error;
		}
	}, [setGAUserId, logEventTyped, eventData, onSubmit, clearUserEventTicketReservations]);

	const handleSubmitFreeEvent = useCallback(async (values: CheckoutFormDto): Promise<void> => {
		if (!eventData || !eventId || !instance) {
			return;
		}

		// if (isVerificationRequired) {
		// 	if (checkoutStep === paymentStep) {
		// 		await onSendVerificationCode(phone);
		// 		return;
		// 	}

		// 	if (checkoutStep === verifyCodeStep && ((!verification && isSmsEnabled) || isSignIn === null)) {
		// 		return;
		// 	}
		// }

		// paid event ($0 price)
		if (eventData.isPaid) {
			if (isPaymentRequired) {
				return;
			}

			const query = new URLSearchParams(window.location.search);
			const paymentIntent = await createPaymentIntent({
				...values,
				e2eToken: isE2ETest ? query.get('e2eToken') : undefined,
			});
			await onEventSubmit(paymentIntent.orderId);

			return;
		}

		// free event
		try {
			const query = new URLSearchParams(window.location.search);
			const publicJoinData: PublicJoinEventBody = {
				...values,
				e2eToken: isE2ETest ? query.get('e2eToken') : undefined,
				intakeFormResults: values.intakeForms ? prepareIntakeFormResultsToSave(values.intakeForms)[0] : undefined,
			};

			const eventToUser = await eventsService.publicJoinEvent(eventId, instance, EventToUserPeriodTypeEnum.SingleDay, publicJoinData);

			if (!eventToUser) {
				enqueueSnackbar('This user is already joined', { variant: 'error' });

				return;
			}

			await onEventSubmit(eventToUser.orderId);
		} catch (error) {
			if (error instanceof AxiosError) {
				if (error.response?.status === 403) {
					setShowRecaptchaCheckbox(error.response?.status === 403);
				}

				const msg = error.response?.data?.message || error.message;
				enqueueSnackbar(msg, { variant: 'error' });
				setRegistrationFailed(true);
			} else {
				enqueueSnackbar(error.message, { variant: 'error' });
				setRegistrationFailed(true);
			}

			throw error;
		}
	}, [
		eventId,
		instance,
		eventData,
		enqueueSnackbar,
		onEventSubmit,
		createPaymentIntent,
		prepareIntakeFormResultsToSave,
		isPaymentRequired,
		isE2ETest,
	]);

	const validationSchema = useMemo(() => {
		const validationSchema: Record<string, yup.Schema> = {
			fullName: yup.string().required('First name is required')
				.min(3, 'Full name is too short'),
			email: yup.string().required('Email is required')
				.test({
					message: 'Invalid email',
					test: value => isEmail(value),
				}),
			phone: yup.string().required('Phone number is required')
				.test({
					message: 'Invalid phone number',
					test: value => isPhoneNumber(value, null),
				}),
		};

		const intakeFormValidationSchemas = compact(
			map(Array.from({ length: ticketsQuantity || 1 }), (_, index) => {
				if (!intakeFormFields.length || !eventData) {
					return null;
				}

				const intakeFormStep = index + 1;

				const eventPriceId = findKey(
					eventPricesSteps,
					({ firstStep, lastStep }) => (firstStep <= intakeFormStep && intakeFormStep <= lastStep),
				);

				const intakeFormResultValidationSchema: Record<string, yup.Schema> = intakeFormFields.reduce((acc, field) => {
					let validation = field.type === 'check-box'
						? yup.boolean()
						: field.type === 'checkbox-list'
							? yup.array()
							: field.type === 'date-picker'
								? yup.date() : yup.string();

					if (
						(eventPriceId && field.tierIds && !field.tierIds.includes(eventPriceId))
						|| (intakeFormStep !== 1 && !intakeFormReservedFieldNames.includes(field.name))
						|| (checkoutStep < intakeFormStep && intakeFormReservedFieldNames.includes(field.name))
					) {
						return { ...acc, [field.name]: validation.optional() };
					}

					if (field.required) {
						validation = validation.required(`${field.name} is required`);

						if (validation instanceof yup.BooleanSchema) {
							validation = validation.oneOf([true], `${field.name} must be checked`);
						}
					} else {
						validation = validation.optional();
					}

					if (field.type === 'text-input' && validation instanceof yup.StringSchema) {
						if (field.textInputType === 'email') {
							validation = validation.test({
								message: 'Invalid email',
								test: value => isEmail(value),
							});
						}

						if (field.textInputType === 'phone-number') {
							validation = validation.test({
								message: 'Invalid phone number',
								test: value => (!field.required && !value) || isPhoneNumber(value, null),
							});
						}

						if (field.validateIsEqualToField) {
							validation = validation.oneOf([yup.ref(field.validateIsEqualToField), ''], 'Incorrect value');
						}
					}

					return { ...acc, [field.name]: validation };
				}, {});

				if (!intakeFormSettings?.waiverSections?.length && (intakeFormSettings?.waiverHtml || eventData?.waiverUrl)) {
					let waiverAgreementSchema = yup.boolean();
					let participantInitialsSchema = yup.string();

					if (intakeFormStep === 1) {
						waiverAgreementSchema = waiverAgreementSchema
							.required('Waiver agreement is required')
							.oneOf([true], 'Waiver agreement must be checked');
						participantInitialsSchema = participantInitialsSchema.required('Participant initials are required');
					} else {
						waiverAgreementSchema = waiverAgreementSchema.optional();
						participantInitialsSchema = participantInitialsSchema.optional();
					}

					intakeFormResultValidationSchema.waiverAgreement = waiverAgreementSchema;
					intakeFormResultValidationSchema.participantInitials = participantInitialsSchema;
				}

				return {
					isPrefilled: yup.boolean().optional(),
					result: yup.object().shape(intakeFormResultValidationSchema)
				};
			})
		);

		if (intakeFormValidationSchemas.length) {
			validationSchema.intakeForms = yup.tuple(
				intakeFormValidationSchemas.map(schema => yup.object().shape(schema)) as any
			);
		}

		return yup.object().shape(validationSchema);
	}, [
		eventData,
		checkoutStep,
		eventPricesSteps,
		intakeFormReservedFieldNames,
		ticketsQuantity,
		intakeFormFields,
		intakeFormSettings?.waiverHtml,
		intakeFormSettings?.waiverSections,
	]);

	const initialValues = {
		fullName: '',
		email: '',
		phone: '+1',
		recaptchaTokenV2: undefined,
		recaptchaTokenV3: undefined,
	}

	const formik = useFormik<CheckoutFormDto>({
		initialValues,
		validateOnChange,
		onSubmit: async values => {
			if (isTiersEditable) {
				setIsTearsEditable(false);
			}

			// pick tickets -> intakeForm(s) -> payment -> [verification] -> order success

			if (checkoutStep === paymentStep || checkoutStep === verifyCodeStep) {
				await handleSubmitFreeEvent(values);
			}

			if (checkoutStep !== verifyCodeStep) {
				setCheckoutStep(checkoutStep + 1);

				if (formBodyRef.current) {
					formBodyRef.current.scrollTop = 0;
				}
			}
		},
		validationSchema
	});
	const {
		values,
		errors,
		resetForm,
		handleChange,
		setFieldValue,
		validateForm,
		isSubmitting,
		setSubmitting,
		submitForm,
	} = formik;

	const firstIntakeForm = useMemo(() => values.intakeForms?.[0], [values.intakeForms]);
	const defaultIntakeForm = useMemo(() => calculateDefaultIntakeForm(firstIntakeForm, intakeFormCommonFieldNames), [firstIntakeForm, intakeFormCommonFieldNames]);

	const handleValidateForm = useCallback(async (): Promise<FormikErrors<CheckoutFormDto>> => {
		const errors = await validateForm();

		if (!isEmpty(errors)) {
			let key = Object.keys(errors)[0];
			const error = errors[key];

			if (Array.isArray(error) && error.length) {
				const intakeFormErrors = compact(error)[0] ?? {};
				key = Object.keys(intakeFormErrors)[0] ?? key;
			}

			const element = document.getElementById(key);
			element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
			setValidateOnChange(true);
		}

		return errors;
	}, [validateForm]);

	// track event on registration form opened
	useEffect(() => {
		if (eventData) {
			logEventTyped('registration_form_opened', {
				eventType: eventData.isPaid ? 'paid' : 'free',
				pageType: 'event',
				baseEventId: eventData.baseId,
				eventInstance: eventData.instance instanceof Date ? eventData.instance?.toISOString() : eventData.instance,
			});
		}
	},[])

	// Reset intake forms
	useEffect(() => {
		resetForm({
			values: {
				fullName: '',
				email: '',
				phone: '+1',
				intakeForms: new Array(preselectedTicketsQuantity || 1)
					.fill(null)
					.map((_val, index) => ({
						isPrefilled: index > 0 ? !!intakeFormSettings?.showSectionsOnceForMultipleTickets : false,
						result: {}
					})),
				recaptchaTokenV2: undefined,
				recaptchaTokenV3: undefined,
			}
		});
	}, [intakeFormSettings?.showSectionsOnceForMultipleTickets, preselectedTicketsQuantity, resetForm, getPrefilledIntakeFormResults]);

	// Update the price and fees after changing the ticket quantity.
	useEffect(() => {
		if (eventData?.isPaid && !isTiersEditable && ticketsQuantity > 0) {
			cancelFunc();
			getPriceAndFees(isPromocodeApplied ? promocode : undefined);
			upsertUserEventTicketReservations();
		}
	}, [eventData?.isPaid, isTiersEditable, ticketsQuantity]);

	useEffect(() => {
		if (newPriceAndFees && !isPriceAndFeesLoading) {
			setPriceAndFees(newPriceAndFees);
		}
	}, [newPriceAndFees, isPriceAndFeesLoading]);

	useEffect(() => {
		if (checkoutStep !== verifyCodeStep) {
			return;
		}

		const interval = setInterval(() => {
			setResetCodeTimer(value => (value > 0 ? value - 1 : null));
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [checkoutStep, verifyCodeStep]);

	const onBackButtonClick = useCallback(() => {
		if (checkoutStep === verifyCodeStep) {
			setVerification(null);
			setFieldValue('code', '');
		}

		if (checkoutStep > 1) {
			setCheckoutStep(checkoutStep - 1);

			if (formBodyRef.current) {
				formBodyRef.current.scrollTop = 0;
			}
		}
	}, [checkoutStep, setFieldValue, verifyCodeStep]);

	const handleSubmitForm = useCallback(async (): Promise<void> => {
		setSubmitting(true);
		try {
			const errors = await handleValidateForm();
			const { intakeForms, ...mainFormErrors } = errors;

			const combinedFields = {
				...mainFormErrors,
				...(typeof intakeForms?.[0] === 'object' && 'result' in intakeForms[0] ? intakeForms[0].result : {})
			};

			if (!isEmpty(errors)) {
				Object.entries(combinedFields).forEach(([key, value], index) => {
					logEventTyped('registration_validation_failed', {
						fieldName: key,
						validationError: value,
						errorCount: index + 1,
					});
				})

				return;
			}

			await submitForm();
		} finally {
			setSubmitting(false);
		}
	}, [handleValidateForm, submitForm, setSubmitting]);

	const handlePay = useCallback(async (elements: StripeElements, selectedPaymentMethodType: string, isVerificationRequired?: boolean): Promise<void> => {
		setIsPaying(true);

		try {
			const errors = await handleValidateForm();

			if (!isEmpty(errors)) {
				return;
			}

			// if (isVerificationRequired && !values.code) {
			// 	handleSubmitForm();

			// 	return;
			// }

			setValidateOnChange(false);
			const paymentIntent = await createPaymentIntent(values);

			if (paymentIntent.intent) {
				console.debug('selectedPaymentMethodType', selectedPaymentMethodType);

				const response = await stripeService.handleConfirmPayment(
					selectedPaymentMethodType,
					elements,
					paymentIntent,
					values,
				);

				if (!response) {
					enqueueSnackbar('Payment failed', { variant: 'error' });
					return;
				}

				if (response.error) {
					enqueueSnackbar(response.error.message, { variant: 'error' });

					return;
				}

				if (response.paymentIntent.status === 'succeeded') {
					await onEventSubmit(paymentIntent.orderId);

					const { intakeForms, recaptchaTokenV2, recaptchaTokenV3, ...mainFormValues } = values;
					const combinedFields = {
						...mainFormValues,
						...(typeof intakeForms?.[0] === 'object' && 'result' in intakeForms[0] ? intakeForms[0].result : {})
					};

					logEventTyped('registration_form_submitted', {
						currency: response.paymentIntent.currency,
						totalAmount: response.paymentIntent.amount,
						trasactionId: response.paymentIntent.id,
						paymentMethod: response.paymentIntent.payment_method as string,
						baseEventId: eventData?.baseId,
						fieldsCompleted: Object.keys(combinedFields).filter(key => combinedFields[key] !== initialValues[key]),
						fieldsIncomplete: Object.keys(combinedFields).filter(key => combinedFields[key] === initialValues[key]),
						eventInstance: eventData?.instance instanceof Date ? eventData?.instance?.toISOString() : eventData?.instance,
					});
				}
			}
		} finally {
			setIsPaying(false);
		}
	}, [createPaymentIntent, enqueueSnackbar, onEventSubmit, handleValidateForm, values]);

	const handleMockPayForE2E = useCallback(async (): Promise<void> => {
		if (!isE2ETest) {
			throw new Error('This action is not allowed in this environment');
		}

		setIsPaying(true);

		try {
			const errors = await handleValidateForm();

			if (!isEmpty(errors)) {
				return;
			}

			setValidateOnChange(false);

			await handleE2ECardPayment({
				createPaymentIntent: async (e2eToken) => await createPaymentIntent({
					...values,
					e2eToken,
				}),
				// E2E payment - payment intent is already confirmed
				onMockSubmit: async (paymentIntent) => onEventSubmit(paymentIntent.orderId),
			});
		} catch (error) {
			enqueueSnackbar(error.message, { variant: 'error' });

			throw error;
		} finally {
			setIsPaying(false);
		}
	}, [isE2ETest, createPaymentIntent, enqueueSnackbar, onEventSubmit, handleValidateForm, values]);

	const openMembershipFormModal = useCallback((): void => {
		if (!publicHostMemberships?.length) {
			return;
		}

		setMembershipFormModalIsOpen(true);
	}, [publicHostMemberships?.length]);
	const closeMembershipFormModal = useCallback((): void => {
		setMembershipFormModalIsOpen(false);
	}, []);

	const openMembershipOrderModalIsOpen = useCallback(async (order?: OrderDto): Promise<void> => {
		if (!order) {
			return;
		}

		await refetchUserMemberships?.();
		setMembershipOrder(order);
		setMembershipOrderModalIsOpen(true);
	}, [refetchUserMemberships]);
	const closeMembershipOrderModalIsOpen = useCallback((): void => {
		setMembershipOrderModalIsOpen(false);
		setMembershipOrder(null);
	}, []);

	const openEventBundleFormModal = useCallback((): void => {
		if (!hostEventBundles?.length) {
			return;
		}

		setEventBundleFormModalIsOpen(true);
	}, [hostEventBundles?.length]);
	
	const closeEventBundleFormModal = useCallback((): void => {
		setEventBundleFormModalIsOpen(false);
	}, []);

	const openEventBundleOrderModalIsOpen = useCallback(async (order?: OrderDto): Promise<void> => {
		if (!order) {
			return;
		}

		await refetchUserEventBundles?.();
		setEventBundleOrder(order);
		setEventBundleOrderModalIsOpen(true);
	}, [refetchUserEventBundles]);

	const closeEventBundleOrderModal = useCallback((): void => {
		setEventBundleOrderModalIsOpen(false);
		setEventBundleOrder(null);
	}, []);

	return (
		<FormikProvider value={formik}>
			<Box sx={styles.root}>
				<Showable hide={membershipFormModalIsOpen}>
					<EventBackground
						background={background && getPublicFileUrl(background, {
							variant: FileVariantsEnum.XS,
						})}
					/>
				</Showable>
				<Box sx={styles.content}>
					{Header && (
						<Header clearUserEventTicketReservations={clearUserEventTicketReservations}/>
					)}

					<Box sx={styles.body} id="checkout-modal-body">
						<Showable
							show={
								publicHostMemberships?.length && hostData && isPaidEvent
								&& !boughtMembership && !signInFormIsVisible
							}
						>
							{() => (
								<Box sx={styles.hostMemberships}>
									<UserAvatar user={hostData} sx={{ width: '24px', height: '24px' }} />
									<Typography sx={styles.hostMembershipsText}>
										Join {hostData.fullName} Membership to get your special offer
									</Typography>
									<Box
										sx={styles.hostMembershipsLink}
										onClick={() => {
											openMembershipFormModal();
										}}
									>
										Learn More
									</Box>
								</Box>
							)}
						</Showable>
						<Showable
							show={
								hostEventBundles?.length 
								&& !hostMemberships?.length
								&& hostData 
								&& isPaidEvent
								&& !boughtEventBundle 
								&& !signInFormIsVisible
							}
						>
							{() => (
								<Box sx={styles.hostMemberships}>
									<UserAvatar user={hostData} sx={{ width: '24px', height: '24px' }} />
									<Typography sx={styles.hostMembershipsText}>
										Get {hostData.fullName} Event Bundle to join events for free
									</Typography>
									<Box
										sx={styles.hostMembershipsLink}
										onClick={() => {
											openEventBundleFormModal();
										}}
									>
										Learn More
									</Box>
								</Box>
							)}
						</Showable>
						<Box ref={formBodyRef} sx={styles.formBody}>
							<Showable hide={hideEventDetails}>
								<EventDetails
									{...eventDetailsProps}
									clearTickets={clearTickets}
									onTicketAdd={addTicket}
									onTicketRemove={removeTicket}
									hideOrderDetails={signInFormIsVisible}
									checkoutStep={checkoutStep}
									eventData={eventData}
									eventPricesSteps={eventPricesSteps}
									defaultIntakeForm={defaultIntakeForm}
									reservationTimer={reservationTimer}
									ticketsQuantity={ticketsQuantity}
									firstIntakeForm={firstIntakeForm}
									isPaying={isPaying}
									ticketIsReserved={ticketIsReserved}
									isPromocodeApplied={isPromocodeApplied}
									tickets={tickets}
									isPriceAndFeesLoading={isPriceAndFeesLoading}
									isTiersEditable={isTiersEditable}
									priceAndFees={priceAndFees}
									promocode={promocode}
									showSkeleton={showSkeleton}
									onEditTiersButtonClick={() => {
										setIsTearsEditable(!isTiersEditable);
									}}
								/>
							</Showable>
							<Box sx={{ flex: { md: 1 } }}>
								<Showable show={signInFormIsVisible}>
									<SignInForm
										sx={styles.form}
										contentSx={{ p: `0 !important` }}
										footerSx={{ backgroundColor: 'transparent', p: 0 }}
										onBackButtonClick={() => {
											setSignInFormIsVisible(false);
										}}
										onSuccess={() => {
											cancelFunc();
											getPriceAndFees(isPromocodeApplied ? promocode : undefined);
											setSignInFormIsVisible(false);
										}}
									/>
								</Showable>
								<Showable hide={signInFormIsVisible}>
									<Box sx={styles.form}>
										<Showable show={showSkeleton}>
											<Box display="flex" flexDirection="column" gap="31px">
												<Skeleton
													sx={{ height: '20px', width: '98px', borderRadius: '12px', backgroundColor: Colors.white_a30 }}
													variant="rectangular"
												/>
												<Box display="flex" flexDirection="column" gap="11px">
													<Skeleton
														sx={{ height: '14px', width: '98px', borderRadius: '12px', backgroundColor: Colors.white_a20 }}
														variant="rectangular"
													/>
													<Skeleton
														sx={{ height: '46px', width: '100%', borderRadius: '12px', backgroundColor: Colors.white_a5 }}
														variant="rectangular"
													/>
												</Box>
												<Box display="flex" flexDirection="column" gap="11px">
													<Skeleton
														sx={{ height: '14px', width: '98px', borderRadius: '12px', backgroundColor: Colors.white_a20 }}
														variant="rectangular"
													/>
													<Skeleton
														sx={{ height: '46px', width: '100%', borderRadius: '12px', backgroundColor: Colors.white_a5 }}
														variant="rectangular"
													/>
												</Box>
												<Box display="flex" flexDirection="column" gap="11px" sx={{ opacity: 0.4 }}>
													<Skeleton
														sx={{ height: '14px', width: '98px', borderRadius: '12px', backgroundColor: Colors.white_a20 }}
														variant="rectangular"
													/>
													<Skeleton
														sx={{ height: '46px', width: '100%', borderRadius: '12px', backgroundColor: Colors.white_a5 }}
														variant="rectangular"
													/>
												</Box>
												<Box display="flex" flexDirection="column" gap="11px" sx={{ opacity: 0.2 }}>
													<Skeleton
														sx={{ height: '14px', width: '98px', borderRadius: '12px', backgroundColor: Colors.white_a20 }}
														variant="rectangular"
													/>
													<Skeleton
														sx={{ height: '46px', width: '100%', borderRadius: '12px', backgroundColor: Colors.white_a5 }}
														variant="rectangular"
													/>
												</Box>
											</Box>
										</Showable>
										<Showable show={eventData && !showSkeleton}>
											{() => (
												<React.Fragment>
													<Showable show={checkoutStep > 1}>
														<IconButton
															sx={styles.backButton}
															disabled={isSubmitting}
															onClick={onBackButtonClick}
														>
															<ArrowBackIcon />
														</IconButton>
													</Showable>
													<Showable show={checkoutStep !== verifyCodeStep}>
														<Box display="flex" flexDirection="column" gap="24px">
															<Showable
																show={isMultipleIntakeForm
																	? checkoutStep < paymentStep
																	: paymentStep}
															>
																<Box display="flex" flexDirection="column" gap="4px">
																	<Showable show={!currentUser && hostMemberships?.length && isPaidEvent}>
																		<Typography
																			sx={{
																				fontFamily: 'Montserrat',
																				fontSize: '12px',
																				fontWeight: 600,
																				lineHeight: '15px',
																				color: Colors.white,
																				textAlign: 'end',
																			}}
																		>
																			Member of community?
																		</Typography>
																	</Showable>
																	<Showable
																		show={
																			!currentUser
																			&& isPaidEvent
																			&& hostEventBundles?.length
																			&& !hostMemberships?.length 
																		}
																	>
																		<Typography
																			sx={{
																				fontFamily: 'Montserrat',
																				fontSize: '12px',
																				fontWeight: 600,
																				lineHeight: '15px',
																				color: Colors.white,
																				textAlign: 'end',
																			}}
																		>
																			Have {hostData.fullName} Event Bundle?
																		</Typography>
																	</Showable>
																	<Box display="flex" alignItems="center" gap="10px" justifyContent="space-between">
																		<Box display="flex" alignItems="center" gap="10px" flex={1}>
																			<Typography sx={commonStyles.title}>
																				{isMultipleIntakeForm ? `Attendee ${checkoutStep}` : 'Your info'}
																			</Typography>
																			<Showable show={currentEventPrice && isMultipleIntakeForm}>
																				<Typography sx={styles.ticketType}>
																					{currentEventPrice?.tierName}
																				</Typography>
																			</Showable>
																		</Box>
																		<Showable
																			show={
																				!currentUser
																				&& (hostMemberships?.length || hostEventBundles?.length)
																				&& isPaidEvent
																			}
																		>
																			<Button
																				sx={commonStyles.secondaryButton}
																				onClick={() => { setSignInFormIsVisible(true); }}
																			>
																				Sign in here
																			</Button>
																		</Showable>
																	</Box>
																</Box>
																<TicketForm
																	checkoutStep={checkoutStep}
																	paymentStep={paymentStep}
																	currentIntakeFormIndex={currentIntakeFormIndex}
																	intakeFormFields={intakeFormFields}
																	currentEventPrice={currentEventPrice}
																	eventData={eventData}
																	intakeFormSettings={intakeFormSettings}
																	defaultIntakeForm={defaultIntakeForm}
																	firstIntakeForm={firstIntakeForm}
																	eventPricesSteps={eventPricesSteps}
																	disabled={isSubmitting || isPaying}
																/>
															</Showable>
															<Showable show={checkoutStep === paymentStep}>
																<PaymentStep
																	paymentMethodTypes={paymentMethodTypes}
																	minDonation={minDonation}
																	eventData={eventData}
																	promocode={promocode}
																	ticketsQuantity={ticketsQuantity}
																	isPaymentRequired={isPaymentRequired}
																	priceAndFees={priceAndFees}
																	isPolicyAgreed={isPolicyAgreed}
																	donationAmount={donationAmount}
																	isPaying={isPaying}
																	isE2ETest={isE2ETest}
																	showRecaptchaCheckbox={showRecaptchaCheckbox}
																	isPriceAndFeesLoading={isPriceAndFeesLoading}
																	isPromocodeApplied={isPromocodeApplied}
																	handlePay={handlePay}
																	cancelFunc={cancelFunc}
																	setPromocode={setPromocode}
																	debounceFunc={debounceFunc}
																	getPriceAndFees={getPriceAndFees}
																	setDonationAmount={setDonationAmount}
																	handleMockPayForE2E={handleMockPayForE2E}
																/>
															</Showable>
														</Box>
													</Showable>
													<Showable show={checkoutStep === verifyCodeStep}>
														<Box display="flex" flexDirection="column" gap="8px" mb="24px">
															<Typography sx={styles.verificationTitle}>Confirm phone number</Typography>
															<Typography sx={styles.verificationDescription}>
																{`Enter code that we have sent you on `}
																<Box component="span" fontWeight={600}>
																	{values.phone}
																</Box>
															</Typography>
														</Box>
														<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
															<CoreTextField
																fullWidth
																placeholder="Code"
																disabled={isSubmitting}
																value={values.code}
																name="code"
																autoComplete="one-time-code"
																onChange={handleChange}
																error={!!errors.code}
																helperText={errors.code}
															/>
															<Typography
																sx={{
																	fontFamily: 'Montserrat',
																	fontStyle: 'normal',
																	fontWeight: 400,
																	fontSize: '13px',
																	lineHeight: '16px',
																	color: '#FFFFFF',
																}}
															>
																{resendCodeTimer ? (
																	<Box component="span">Resend in {resendCodeTimer}.</Box>
																) : (
																	<Box
																		component="span"
																		sx={!isSubmitting ? {
																			'&:hover': {
																				cursor: 'pointer',
																				textDecoration: 'underline'
																			}
																		} : undefined}
																		onClick={() => {
																			if (!isSubmitting) {
																				onSendVerificationCode(values.phone);
																			}
																		}}
																	>
																		Resend code
																	</Box>
																)}
															</Typography>
														</Box>
													</Showable>
													<Showable hide={isPaymentRequired && checkoutStep === paymentStep}>
														<Box sx={{ display: 'flex', justifyContent: 'center', mt: '24px' }}>
															<Button
																id={checkoutFromSubmitButtonId}
																sx={commonStyles.submitButton}
																disabled={
																	!isPolicyAgreed || isPaymentIntentLoading || isSubmitting
																	|| !ticketsQuantity || registrationFailed || isPriceAndFeesLoading
																}
																onClick={handleSubmitForm}
															>
																{isSubmitting
																	? <CircularProgress />
																	: checkoutStep === paymentStep
																		? 'Register'
																		: checkoutStep === verifyCodeStep
																			? 'Continue'
																			: 'Next'}
															</Button>
														</Box>
													</Showable>
												</React.Fragment>
											)}
										</Showable>
									</Box>
									<Showable show={showSkeleton}>
										<Box display="flex" justifyContent="center" mt="8px">
											<Skeleton
												sx={{ height: '8px', width: '70%', borderRadius: '12px', backgroundColor: Colors.white_a30 }}
												variant="rectangular"
											/>
										</Box>
									</Showable>
									<Showable show={checkoutStep !== verifyCodeStep && !showSkeleton}>
										<Box sx={{ mt: '16px', display: 'flex', justifyContent: 'center' }}>
											<FormControlLabel
												sx={commonStyles.checkBox}
												control={
													<Checkbox
														id="privacy-policy"
														disabled={isSubmitting || isPaying}
														checked={isPolicyAgreed}
														onChange={() => { setIsPolicyAgreed(!isPolicyAgreed); }}
														checkedIcon={<CheckRounded />}
													/>
												}
												label={<>By continuing, you agree to our <a href="/privacy-policy.html" target='_blank'>Privacy Policy</a> and <a href="/terms.html" target='_blank'>Terms & Conditions</a></>}
											/>
										</Box>
									</Showable>
								</Showable>
							</Box>
						</Box>
					</Box>
				</Box>
				<Showable show={publicHostMemberships?.length && hostData && membershipFormModalIsOpen}>
					{() => (
						<MembershipFormModal
							isOpen={membershipFormModalIsOpen}
							onClose={closeMembershipFormModal}
							hostData={hostData}
							onSubmit={openMembershipOrderModalIsOpen}
							hostMemberships={hostMemberships}
						/>
					)}
				</Showable>
				<OrderModal
					open={membershipOrderModalIsOpen}
					onClose={closeMembershipOrderModalIsOpen}
					orderId={membershipOrder?.id}
					host={hostData}
					hostEvents={hostEvents}
					userMembership={boughtMembership}
				/>
				<Showable show={hostEventBundles?.length && hostData && eventBundleFormModalIsOpen}>
					{() => (
						<EventBundleFormModal
							isOpen={eventBundleFormModalIsOpen}
							onClose={closeEventBundleFormModal}
							hostData={hostData}
							onSubmit={openEventBundleOrderModalIsOpen}
							hostEventBundles={hostEventBundles}
						/>
					)}
				</Showable>
				<OrderModal
					open={eventBundleOrderModalIsOpen}
					onClose={closeEventBundleOrderModal}
					orderId={eventBundleOrder?.id}
					host={hostData}
					hostEvents={hostEvents}
					userEventBundle={boughtEventBundle}
				/>
				<Showable show={warningModalType}>
					<WarningModal
						open={true}
						title={warningModalType === WarningModalTypeEnum.TicketReservations ? "Oh, no!" : "Processing Your Payment…"}
						text={
							warningModalType === WarningModalTypeEnum.TicketReservations
								? "Your ticket(s) have been claimed by someone else as the reservation time has ended. Please select another ticket(s) to continue."
								: "Your payment is on its way! We're finalizing the details, and you’ll receive your ticket(s) via email shortly."
						}
						onClose={() => {
							clearUserEventTicketReservations();
							onWarningModalClose?.();
							setWarningModalType(null);
						}}
					/>
				</Showable>
				<WarningModal
					open={isSomePriceInvalid}
					text="Ticket details have recently changed. Please refresh to see the latest updates and proceed."
					submitButtonText="Refresh"
					disableClose
					onSubmit={async (): Promise<void> => {
						await refetchEvent();
						setIsSomePriceInvalid(false);
						onWarningModalClose?.();
					}}
				/>
			</Box>
		</FormikProvider>
	);
};
